/**
 * a2ui WIDGET grammar functions (DEC-6) — the deterministic, invertible mapping
 * between the viewIr bind/when/connection grammar and a2ui 1.0 Dynamic values +
 * rendererOnly functions, plus the `rendererOnly` function registry itself.
 *
 * The mapping is a bijection on the shapes we emit, so `convert.js` (forward) and
 * the runtime wrapper in `components.js` (inverse) share this one file and stay
 * consistent by construction — the property the parity tests lean on.
 *
 *   dotted bind      ⇄ RFC-6901 path              (`entries[0].name` ⇄ `/entries/0/name`)
 *   `{interp}` string ⇄ formatString(`${…}`)
 *   `{agg}`          ⇄ vi_agg(...)
 *   cycle/map/format ⇄ vi_cycle / vi_map / vi_format
 *   `when`           ⇄ visible (vi_<op>)
 *   connections      ⇄ on_<signal> ActionList (vi_deliver / vi_setData / host_*)
 */

// ─── the rendererOnly function catalog (names + doc) ───────────────────────

/** viewIr `when` op → a2ui rendererOnly predicate function. */
export const WHEN_OP_TO_FN = {
  nonempty: 'vi_nonempty', empty: 'vi_empty', eq: 'vi_eq', neq: 'vi_neq', in: 'vi_in',
  gt: 'vi_gt', gte: 'vi_gte', lt: 'vi_lt', lte: 'vi_lte',
};
export const FN_TO_WHEN_OP = Object.fromEntries(Object.entries(WHEN_OP_TO_FN).map(([k, v]) => [v, k]));

/** The 19 host bridge slots (HostAdapter.HOST_SLOTS) → host_* rendererOnly functions. */
export const HOST_SLOTS = [
  'openItem', 'openPage', 'openSession', 'openMainSession', 'openCapture', 'closePage',
  'logData', 'pickMedia', 'crudRemove', 'itemOptions', 'imageTap', 'attachItem', 'reloadData',
  'setSubview', 'setSessionBadge', 'download', 'openUrl', 'copyText', 'declareNavAction',
];
/** host slots that need a user gesture (a2ui `requiresUserActivation`). */
export const HOST_ACTIVATION_SLOTS = new Set(['openUrl', 'download', 'copyText', 'openCapture', 'pickMedia', 'imageTap']);

export const RENDERER_ONLY_FUNCTIONS = [
  ...Object.values(WHEN_OP_TO_FN),
  'vi_agg', 'vi_cycle', 'vi_map', 'vi_format', 'vi_deliver', 'vi_setData', 'vi_when',
  ...HOST_SLOTS.map((s) => `host_${s}`),
];

// ─── path <-> RFC-6901 ─────────────────────────────────────────────────────

/**
 * viewIr dotted/bracketed bind → an RFC-6901 pointer. Scope roots `item`/`index`
 * become RELATIVE paths (a2ui resolves them against the collection scope):
 *   `item.name`  → `name`      (relative)
 *   `item`       → ``          (the row itself)
 *   `index`      → handled by the caller as `@index()`
 *   `entries[0].name` / `entries.0.name` → `/entries/0/name`
 *   `outfits`    → `/outfits`
 */
export function dottedToPointer(expr) {
  const s = String(expr || '').trim();
  if (s === '' ) return '/';
  const segs = s.replace(/\[(\d+)\]/g, '.$1').split('.').filter((x) => x !== '');
  if (segs[0] === 'item') {
    const rest = segs.slice(1);
    return rest.join('/'); // relative (may be '' for the row itself)
  }
  return '/' + segs.map(escapeSeg).join('/');
}

/** RFC-6901 pointer → viewIr dotted path (inverse). */
export function pointerToDotted(pointer) {
  const p = String(pointer || '');
  if (p === '' || p === '/') return 'item';
  if (!p.startsWith('/')) {
    // relative → row scope
    return p === '' ? 'item' : `item.${p.split('/').map(unescapeSeg).join('.')}`;
  }
  return p.slice(1).split('/').map(unescapeSeg).join('.').replace(/\.(\d+)/g, '[$1]');
}

function escapeSeg(s) { return String(s).replace(/~/g, '~0').replace(/\//g, '~1'); }
function unescapeSeg(s) { return String(s).replace(/~1/g, '/').replace(/~0/g, '~'); }

// ─── interpolation <-> formatString ───────────────────────────────────────

const INTERP_RE = /\{([^{}]+)\}/g;

/** True if a string carries `{…}` interpolation slots. */
export function hasInterpolation(str) {
  return typeof str === 'string' && INTERP_RE.test(str) && (INTERP_RE.lastIndex = 0, true);
}

/** `"{a.b} of {c}"` → a2ui formatString FunctionCall `{call, args:{value:"${…}"}}`. */
export function interpToFormatString(str) {
  const value = String(str).replace(INTERP_RE, (_m, expr) => `\${${dottedToPointer(expr.trim())}}`);
  return { call: 'formatString', args: { value } };
}

/** a2ui formatString value string → the viewIr `{a.b}` interpolation (inverse). */
export function formatStringToInterp(value) {
  return String(value).replace(/\$\{([^}]+)\}/g, (_m, ptr) => `{${pointerToDotted(ptr.trim())}}`);
}

// ─── bind (with cycle/map/format ladder + agg) <-> Dynamic ─────────────────

/**
 * viewIr bind → a2ui Dynamic value. Accepts a dotted string, `{$}`/`{bind}`,
 * `{agg}`, and a cycle/map/format transform ladder layered on the base read.
 * A plain string with `{…}` becomes a formatString; a plain string that is a
 * literal stays a literal.
 */
export function bindToDynamic(bind) {
  if (bind == null) return bind;
  if (typeof bind === 'string') {
    if (hasInterpolation(bind)) return interpToFormatString(bind);
    if (isPathString(bind)) return baseRead(bind);
    return bind; // literal
  }
  if (typeof bind !== 'object' || Array.isArray(bind)) return bind;

  let base;
  if ('agg' in bind) base = aggToCall(bind.agg);
  else if ('$' in bind) base = baseRead(bind.$);
  else if ('bind' in bind) base = baseRead(bind.bind);
  else return bind; // not a bind spec

  // transform ladder cycle → map → format (the fixed viewIr order)
  if ('cycle' in bind) base = { call: 'vi_cycle', args: { value: base, table: bind.cycle } };
  if ('map' in bind) {
    const args = { value: base, table: bind.map };
    if ('fallback' in bind) args.fallback = bind.fallback;
    base = { call: 'vi_map', args };
  }
  if ('format' in bind) base = { call: 'vi_format', args: { value: base, format: bind.format } };
  // A bind's `fallback` is its DEFAULT when the bound value resolves empty (a
  // PageShell title bound to `settings.instance_title` falls back to the widget
  // name). `vi_map` carries its own, but a plain path bind had nowhere to put it
  // and silently lost it — so every such default vanished on the a2ui cohort.
  // Same renderer-only convention as vi_cycle/vi_map/vi_format, applied LAST so
  // it defaults the final value.
  if ('fallback' in bind && !('map' in bind)) {
    base = { call: 'vi_fallback', args: { value: base, fallback: bind.fallback } };
  }
  return base;
}

function baseRead(expr) {
  const s = String(expr || '').trim();
  if (s === 'index') return { call: '@index', args: {} };
  return { path: dottedToPointer(s) };
}

function aggToCall(agg) {
  const args = { op: agg.op };
  const of = agg.of == null ? 'entries' : agg.of;
  args.of = Array.isArray(of) ? of.map(dottedToPointer) : dottedToPointer(of);
  if (agg.field != null) args.field = agg.field;
  if (agg.where != null) args.where = agg.where;
  if (agg.suffix != null) args.suffix = agg.suffix;
  return { call: 'vi_agg', args };
}

/** a2ui Dynamic → viewIr bind (inverse of bindToDynamic). */
export function dynamicToBind(dyn) {
  if (dyn == null || typeof dyn !== 'object') return dyn;
  // A viewIr `when` spec nested INSIDE a prop value (a Card badge's
  // `{path, op}`) is not an a2ui Dynamic — Dynamics never carry `op`. Treating
  // it as one dropped the operator and rewrote the path, so a badge that hides
  // when its count is empty rendered "0" instead of disappearing.
  // Its `value` may itself be a BIND (`item.piece_id eq {$: newest.piece_id}`),
  // and returning the spec whole left that inner Dynamic in a2ui form, so the
  // comparison could never match: art-studio's gallery lost its "NEW" badge.
  if ('path' in dyn && 'op' in dyn) {
    return 'value' in dyn ? { ...dyn, value: dynamicToBind(dyn.value) } : dyn;
  }
  // An a2ui pointer Dynamic is `{path}` and NOTHING else. A viewIr spec that
  // happens to carry a `path` key (a `when`, an `awaitConfirm` ladder) brings its
  // siblings along, and reading it as a Dynamic threw them away: nutritionist's
  // goal write lost `{timeout_ms, pendingLabel, successLabel, timeoutLabel}`, so
  // the confirm ladder that tells the user the save landed (CONTRACT §5/§6)
  // silently became a bare read.
  if ('path' in dyn && Object.keys(dyn).length === 1) return { $: pointerToDotted(dyn.path) };
  if (dyn.call === 'formatString') return formatStringToInterp(dyn.args?.value ?? '');
  if (dyn.call === '@index') return { $: 'index' };
  if (dyn.call === 'vi_agg') {
    const a = dyn.args || {};
    const agg = { op: a.op };
    agg.of = Array.isArray(a.of) ? a.of.map(pointerToDotted) : pointerToDotted(a.of);
    if (a.field != null) agg.field = a.field;
    if (a.where != null) agg.where = a.where;
    if (a.suffix != null) agg.suffix = a.suffix;
    return { agg };
  }
  if (dyn.call === 'vi_cycle') return { ...dynamicToBind(dyn.args.value), cycle: dyn.args.table };
  if (dyn.call === 'vi_map') {
    const out = { ...dynamicToBind(dyn.args.value), map: dyn.args.table };
    if ('fallback' in dyn.args) out.fallback = dyn.args.fallback;
    return out;
  }
  if (dyn.call === 'vi_format') return { ...dynamicToBind(dyn.args.value), format: dyn.args.format };
  if (dyn.call === 'vi_fallback') return { ...dynamicToBind(dyn.args.value), fallback: dyn.args.fallback };
  return dyn;
}

function isPathString(s) {
  return /^[A-Za-z_][A-Za-z0-9_]*(\.[A-Za-z0-9_[\]]+|\[[0-9]*\])*$/.test(String(s));
}

// ─── when <-> visible ──────────────────────────────────────────────────────

/** viewIr `when` predicate → a2ui `visible` Dynamic boolean. */
export function whenToVisible(when) {
  if (when == null) return undefined;
  if (when === true || when === false) return when;
  if (typeof when === 'string') return { call: 'vi_nonempty', args: { value: baseRead(when) } };
  if (typeof when === 'object') {
    const path = when.path != null ? when.path : when.of;
    const fn = WHEN_OP_TO_FN[when.op];
    if (!fn) return undefined;
    const args = { value: baseRead(String(path)) };
    if ('value' in when) args.arg = isBindLike(when.value) ? bindToDynamic(when.value) : when.value;
    return { call: fn, args };
  }
  return undefined;
}

/** a2ui `visible` → viewIr `when` (inverse). */
export function visibleToWhen(visible) {
  if (visible == null) return undefined;
  if (visible === true || visible === false) return visible;
  if (typeof visible === 'object' && visible.call) {
    const op = FN_TO_WHEN_OP[visible.call];
    if (!op) return undefined;
    const a = visible.args || {};
    const path = a.value?.path != null ? pointerToDotted(a.value.path) : undefined;
    const when = { path, op };
    if ('arg' in a) when.value = isBindLike(a.arg) ? dynamicToBind(a.arg) : a.arg;
    return when;
  }
  return undefined;
}

function isBindLike(v) {
  return v && typeof v === 'object' && ('$' in v || 'bind' in v || 'path' in v || 'agg' in v || 'call' in v);
}

// ─── connections <-> on_<signal> ActionList ────────────────────────────────

/**
 * One viewIr connection → an a2ui ActionList entry (a functionCall). Widget→host
 * = host_<slot>; `→ page_args.x` = vi_setData; widget→widget = vi_deliver.
 */
export function connectionToAction(conn) {
  const [, signal] = splitPort(conn.from);
  const [target, slot] = splitPort(conn.to);
  const payload = conn.payload ? mapPayload(conn.payload) : undefined;
  let call;
  const args = {};
  if (target === 'host') {
    call = `host_${slot}`;
    if (payload) Object.assign(args, payload);
  } else if (target === 'page_args') {
    call = 'vi_setData';
    args.path = `/page_args/${slot}`;
    if (payload) args.payload = payload;
  } else {
    call = 'vi_deliver';
    args.to = target;
    args.slot = slot;
    if (payload) args.payload = payload;
    if (conn.rate) args.rate = conn.rate;
    if (conn.backpressure) args.backpressure = conn.backpressure;
  }
  const action = { functionCall: { call, args } };
  if (conn.when) action.when = { call: 'vi_when', args: { match: conn.when } };
  // Delivery semantics are NOT derivable from the call. `delivery:"at_least_once"`
  // and `scope:"request"` are what make a host WRITE survive a retry, and `page`
  // names the destination of a navigation — dropping them silently downgraded
  // every write-bearing wire to best-effort. `backpressure`/`rate` ride here too
  // for host targets, whose `args` ARE the payload and cannot hold them.
  const extra = {};
  // `note` is LOAD-BEARING on a host wire, not documentation: `ViewIrConnection
  // .targetFile` parses the destination bundle file out of it
  // ("photo.html, type data_view, args {…}"), and `_shapeOpenPage` needs that
  // file to turn the component's business payload into the host's `{file, args}`.
  // Dropping it left every such wire with NO target, so `host.openPage` fell
  // through to `openPage-without-file` and the card simply did nothing when
  // tapped — a dead affordance that looks perfect in any render comparison.
  for (const k of ['delivery', 'scope', 'page', 'note']) if (conn[k] != null) extra[k] = conn[k];
  // Only `vi_deliver` has somewhere in `args` to put these; a host target's args
  // ARE its payload, and `vi_setData`'s are the path + value.
  if (call !== 'vi_deliver') {
    for (const k of ['rate', 'backpressure']) if (conn[k] != null) extra[k] = conn[k];
  }
  if (Object.keys(extra).length) action.vi_conn = extra;
  return { signal, action };
}

function splitPort(port) {
  const s = String(port || '');
  const dot = s.indexOf('.');
  return dot < 0 ? [s, ''] : [s.slice(0, dot), s.slice(dot + 1)];
}

function mapPayload(payload) {
  const out = {};
  // `signal.x` needs no special case: `dottedToPointer` already yields
  // `/signal/x`. Short-circuiting it here dropped the transform ladder riding on
  // the SAME bind — scene-studio's filter reads `{$:"signal.key", map:{total:"all"
  // …}, fallback:""}` and arrived as the bare key, so the wall filtered on a value
  // its own tab bar never uses.
  for (const [k, v] of Object.entries(payload)) out[k] = payloadValueToDynamic(v);
  return out;
}

/**
 * A connection payload value, unlike a component prop, is LITERAL-FIRST: the kits
 * read the context only for a `{$}`/`{bind}`/`{agg}` envelope or a `"{…}"`
 * interpolation, and a bare string is the string itself — `{"kind":"item"}` means
 * `"item"`, not the current row (view_ir/scope.dart `resolvePayloadValue`, web
 * `resolveProps(conn.payload, null, ctx)`).
 *
 * `bindToDynamic` applies the PROP rule, where a bare path-shaped string IS a read.
 * Using it here turned every path-shaped literal into a data read that resolves to
 * nothing: `host.openPage {file:"index.html"}` became `{path:"/index/html"}` so the
 * host had no file to route on and the card did nothing when tapped;
 * `host.closePage {action:"capture"}` became `{path:"/capture"}` so the action never
 * matched and no session started; `"index"` even became the `@index()` function.
 * The literal survived only when it happened to be un-path-like ("build-outfit.html"
 * has a hyphen) or nested one level down — which is why this looked like a handful
 * of unrelated dead buttons instead of one rule.
 */
function payloadValueToDynamic(v) {
  if (typeof v === 'string') return hasInterpolation(v) ? interpToFormatString(v) : v;
  return bindToDynamic(v);
}

/** a2ui `on_<signal>` ActionList entry → a viewIr connection (inverse). */
export function actionToConnection(fromComp, signal, action) {
  const fc = action.functionCall || {};
  const call = fc.call || '';
  const args = fc.args || {};
  let to;
  const conn = { mode: 'async' };
  if (call.startsWith('host_')) {
    to = `host.${call.slice(5)}`;
    conn.executor = 'host';
    const payload = argsWithout(args, []);
    if (Object.keys(payload).length) conn.payload = unmapPayload(payload);
  } else if (call === 'vi_setData') {
    to = `page_args.${String(args.path || '').split('/').filter(Boolean).slice(1).join('.')}`;
    conn.executor = 'data';
    if (args.payload) conn.payload = unmapPayload(args.payload);
  } else if (call === 'vi_deliver') {
    to = `${args.to}.${args.slot}`;
    conn.executor = 'data';
    if (args.payload) conn.payload = unmapPayload(args.payload);
    if (args.rate) conn.rate = args.rate;
    if (args.backpressure) conn.backpressure = args.backpressure;
  } else {
    return null;
  }
  const out = { from: `${fromComp}.${signal}`, to, ...conn };
  if (action.when?.args?.match) out.when = action.when.args.match;
  if (action.vi_conn && typeof action.vi_conn === 'object') Object.assign(out, action.vi_conn);
  return out;
}

function argsWithout(args, keys) {
  const out = {};
  for (const [k, v] of Object.entries(args)) if (!keys.includes(k)) out[k] = v;
  return out;
}
function unmapPayload(payload) {
  const out = {};
  for (const [k, v] of Object.entries(payload)) {
    if (v && typeof v === 'object' && typeof v.path === 'string' && v.path.startsWith('/signal/')) {
      out[k] = { $: v.path.slice(1).replace(/\//g, '.') };
    } else {
      out[k] = dynamicToBind(v);
    }
  }
  return out;
}
